import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';

const industriesData = {
  'consumer-goods': {
    name: 'Consumer Goods & Electronics',
    tagline: 'High-Velocity Storage for Fast-Moving Products',
    description: 'The consumer goods and electronics sector demands storage solutions that can handle high SKU counts, rapid inventory turnover, and seasonal demand fluctuations. Warehaus+ delivers infrastructure designed for speed and flexibility.',
    image: 'https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxjb252ZXlvciUyMGJlbHQlMjBwYWNrYWdlcyUyMGRpc3RyaWJ1dGlvbiUyMGNlbnRlcnxlbnwwfHx8fDE3NzE4MjI5NTd8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['SKU proliferation and diversity', 'Seasonal demand spikes', 'Fast inventory turnover', 'Product size variations', 'Returns processing'],
    solutions: ['Flexible racking systems', 'Pick modules for efficiency', 'Automated retrieval systems', 'Dynamic slotting capability', 'Returns processing areas'],
    benefits: ['50% faster picking operations', 'Improved inventory accuracy', 'Scalable during peak seasons', 'Reduced labor costs'],
  },
  'automotive': {
    name: 'Automotive',
    tagline: 'Precision Storage for Complex Supply Chains',
    description: 'Automotive supply chains require precise handling of components ranging from small fasteners to large body panels. Our solutions are engineered for the demanding requirements of automotive manufacturing and distribution.',
    image: 'https://images.unsplash.com/photo-1469289759076-d1484757abc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwcGFydHMlMjB3YXJlaG91c2UlMjBzdG9yYWdlJTIwaW5kdXN0cnl8ZW58MHx8fHwxNzcxODIyOTQyfDA&ixlib=rb-4.1.0&q=85',
    challenges: ['Heavy component handling', 'Just-in-time delivery requirements', 'Part traceability needs', 'Large item storage', 'Line-side delivery'],
    solutions: ['Heavy-duty racking systems', 'Cantilever storage for long items', 'ASRS integration', 'Kanban-ready systems', 'Sequencing solutions'],
    benefits: ['JIT delivery compliance', 'Reduced line stoppages', 'Full part traceability', 'Optimized floor space'],
  },
  'food-beverage': {
    name: 'Food & Beverages',
    tagline: 'Temperature-Controlled Excellence',
    description: 'Food and beverage storage requires strict adherence to food safety standards, temperature control, and inventory rotation. Our solutions ensure compliance while maximizing storage efficiency.',
    image: 'https://images.pexels.com/photos/4170172/pexels-photo-4170172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    challenges: ['Cold chain compliance', 'FIFO inventory rotation', 'Hygiene standards', 'Perishable goods handling', 'Regulatory requirements'],
    solutions: ['Drive-in cold storage systems', 'Shuttle racking for FIFO', 'Food-grade materials', 'Temperature monitoring', 'Cleanable designs'],
    benefits: ['FIFO compliance guaranteed', 'Reduced product spoilage', 'Food safety certification ready', 'Energy-efficient cold storage'],
  },
  '3pl-logistics': {
    name: '3PL, Logistics & Warehousing',
    tagline: 'Flexible Infrastructure for Multi-Client Operations',
    description: '3PL providers need infrastructure that can adapt to diverse client requirements. Our modular, rental-based solutions provide the flexibility to scale operations up or down based on demand.',
    image: 'https://images.unsplash.com/photo-1720036236694-d0a231c52563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwcmFja2luZyUyMHN5c3RlbSUyMHN0ZWVsJTIwbWV6emFuaW5lJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzE4MjI5NDB8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['Multi-client operations', 'Variable inventory profiles', 'Scalability requirements', 'Cost management', 'SLA compliance'],
    solutions: ['Modular racking systems', 'Rapid deployment capability', 'Flexible configurations', 'Zone-based designs', 'Scalable infrastructure'],
    benefits: ['Quick client onboarding', 'Reduced capital requirements', 'Flexible contract terms', 'Multi-client optimization'],
  },
  'ecommerce': {
    name: 'E & Q Commerce',
    tagline: 'Built for Speed and Scale',
    description: 'E-commerce and quick commerce demand rapid order fulfillment and the ability to handle massive order volumes. Our solutions are designed for the speed and accuracy that online retail requires.',
    image: 'https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxjb252ZXlvciUyMGJlbHQlMjBwYWNrYWdlcyUyMGRpc3RyaWJ1dGlvbiUyMGNlbnRlcnxlbnwwfHx8fDE3NzE4MjI5NTd8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['High order velocity', 'SKU diversity', 'Same-day delivery pressure', 'Peak season scaling', 'Returns management'],
    solutions: ['Pick tower systems', 'Conveyor integration', 'Micro-fulfillment solutions', 'Goods-to-person systems', 'Returns processing'],
    benefits: ['Sub-hour order processing', 'Same-day delivery capability', 'Peak season ready', '99.9% order accuracy'],
  },
  'pharmaceuticals': {
    name: 'Pharmaceuticals',
    tagline: 'Compliant Storage for Life-Saving Products',
    description: 'Pharmaceutical storage requires strict compliance with regulatory requirements, temperature control, and batch tracking. Our solutions ensure product integrity while maintaining operational efficiency.',
    image: 'https://images.unsplash.com/photo-1694885156873-6a0823e14848?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMGNvbGQlMjBzdG9yYWdlJTIwd2FyZWhvdXNlJTIwc2hlbHZlc3xlbnwwfHx8fDE3NzE4MjI5NDF8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['Regulatory compliance (GDP, GMP)', 'Temperature control', 'Batch tracking requirements', 'Controlled substance security', 'Expiry management'],
    solutions: ['Climate-controlled storage', 'ASRS for accuracy', 'Cleanroom fit-outs', 'Serialization integration', 'Security systems'],
    benefits: ['GDP/GMP compliance', 'Complete batch traceability', 'Zero temperature excursions', 'Audit-ready documentation'],
  },
  'semiconductors': {
    name: 'Semi Conductors',
    tagline: 'Precision Handling for Sensitive Components',
    description: 'Semiconductor manufacturing and distribution requires specialized storage that protects sensitive components from electrostatic discharge, contamination, and environmental factors.',
    image: 'https://images.unsplash.com/photo-1720036236697-018370867320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhdXRvbWF0ZWQlMjB3YXJlaG91c2UlMjBpbnRlcmlvciUyMGRhcmslMjBjaW5lbWF0aWMlMjBuaWdodHxlbnwwfHx8fDE3NzE4MjI5Mzh8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['ESD protection requirements', 'Clean environment needs', 'Precision handling', 'High-value inventory', 'Traceability requirements'],
    solutions: ['ESD-safe storage systems', 'Cleanroom integration', 'Automated handling', 'Climate control', 'Security systems'],
    benefits: ['Zero ESD incidents', 'Cleanroom compliance', 'Full component traceability', 'Protected inventory'],
  },
  'manufacturing': {
    name: 'Manufacturing',
    tagline: 'Production-Integrated Storage Solutions',
    description: 'Manufacturing facilities need storage solutions that integrate seamlessly with production lines. Our solutions optimize the flow of raw materials and finished goods while minimizing production disruptions.',
    image: 'https://images.pexels.com/photos/4170172/pexels-photo-4170172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    challenges: ['Production line integration', 'WIP management', 'Space optimization', 'Material flow efficiency', 'Safety requirements'],
    solutions: ['Line-side storage', 'Kanban systems', 'Mezzanine platforms', 'Flow rack systems', 'Safety barriers'],
    benefits: ['Reduced production delays', 'Optimized material flow', 'Increased floor space', 'Improved safety'],
  },
  'aerospace': {
    name: 'Aerospace / Aviation',
    tagline: 'Specialized Storage for Critical Components',
    description: 'Aerospace and aviation storage requires handling of large, high-value components with complete traceability. Our solutions meet the stringent requirements of the aerospace industry.',
    image: 'https://images.unsplash.com/photo-1770119074920-3c5603188095?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwcmFja2luZyUyMHN5c3RlbSUyMHN0ZWVsJTIwbWV6emFuaW5lJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzE4MjI5NDB8MA&ixlib=rb-4.1.0&q=85',
    challenges: ['Large component storage', 'Complete traceability', 'Compliance requirements', 'MRO operations', 'High-value inventory'],
    solutions: ['Cantilever racking', 'Vertical lift modules', 'Custom storage solutions', 'Climate control', 'Security systems'],
    benefits: ['AS9100 compliance ready', 'Full component history', 'Optimized MRO operations', 'Protected inventory'],
  },
};

export default function IndustryDetail() {
  const { industryId } = useParams();
  const industry = industriesData[industryId];

  if (!industry) {
    return (
      <div className="bg-[#0a1628] pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white mb-4">Industry Not Found</h1>
          <Link to="/industries">
            <Button className="btn-primary">Back to Industries</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a1628] pt-20" data-testid="industry-detail-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${industry.image}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#0a1628]/90 to-[#0a1628]/50" />
        
        <div className="container-custom relative z-10 py-24">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-8">
            <Link to="/industries" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Industries
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-[#E63946]">{industry.name}</span>
          </div>

          <div className="max-w-3xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              {industry.tagline}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tighter mb-6">
              {industry.name}
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {industry.description}
            </p>
            <Link to="/get-quote" data-testid="industry-get-quote">
              <Button className="btn-gold clip-slant h-14 px-10 text-base">
                Get Industry-Specific Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-24 md:py-32 bg-[#0f1e32]" data-testid="challenges-solutions">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Challenges */}
            <div>
              <span className="font-mono text-xs text-red-500 uppercase tracking-widest mb-4 block">
                Industry Challenges
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
                Storage Challenges
              </h2>
              <div className="space-y-4">
                {industry.challenges.map((challenge, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-[#0a1628] border border-red-500/20"
                  >
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Solutions */}
            <div>
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                Warehaus+ Solutions
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
                Our Approach
              </h2>
              <div className="space-y-4">
                {industry.solutions.map((solution, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-[#0a1628] border border-[#E63946]/20"
                  >
                    <CheckCircle className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{solution}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32" data-testid="industry-benefits">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Results
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Expected Outcomes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industry.benefits.map((benefit, index) => (
              <div
                key={index}
                className="industrial-card bg-[#0f1e32] border border-white/10 p-6 text-center hover:border-[#E63946]/50 transition-colors"
              >
                <div className="font-mono text-4xl font-bold text-[#E63946] mb-4">
                  {benefit.split(' ')[0]}
                </div>
                <p className="text-gray-400 text-sm">
                  {benefit.split(' ').slice(1).join(' ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0f1e32]">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0a1628] border border-[#E63946]/30 p-12 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
              Ready to Optimize Your <span className="text-[#E63946]">{industry.name}</span> Storage?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
              Our team of industry experts is ready to design a customized solution that addresses your specific challenges.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-quote" data-testid="industry-detail-get-quote">
                <Button className="btn-gold clip-slant h-14 px-10 text-base">
                  Get a Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/industries">
                <Button className="btn-secondary h-14 px-10 text-base">
                  View All Industries
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
